import type { ReactNode } from "react";
import { useTranslation } from "@thyra/landing-i18n";
import {
  BatteryFull,
  Bot,
  Check,
  ChevronDown,
  House,
  ListChecks,
  ListFilter,
  Loader2,
  type LucideIcon,
  MessageSquareText,
  ShieldCheck,
  Signal,
  Sparkles,
  User,
  Wifi,
  Wrench,
  X,
} from "lucide-react";
import type { ShowcasePhase } from "./WebAppPreview";

/**
 * The mobile app's "Tasks & Approvals" tab inside a phone frame.
 *
 * The app is React Native, so its components cannot be imported here; instead
 * the screen is laid out at the phone's real 375pt width with the app's own
 * NativeWind class strings (which are Tailwind classes) and scaled down to
 * fit. Colour names differ: the app's `brand` / `page` / `borderSubtle` /
 * `mutedForeground` are `primary` / `background` / `border` /
 * `muted-foreground` here, re-pointed by `.phone-surface`. Keep in step with:
 *
 *   screen + task card  apps/mobile/app/(tabs)/tasks.tsx
 *   tab bar             apps/mobile/app/(tabs)/_layout.tsx (NativeTabs: the
 *                       floating system tab bar)
 *   approval cards      apps/mobile/components/approvals/pending-action-card.tsx,
 *                       approval-request-card.tsx
 *   primitives          apps/mobile/components/ui/{badge,card}.tsx
 *
 * Two deliberate liberties, both mirrored in WebAppPreview:
 *   - approvals and human tasks sit in ONE list, the way the web queue already
 *     merges them; the app still splits them with a segmented control.
 *   - a card that goes through a connection leads with that connection's
 *     brand logo where the app shows a generic glyph.
 */

const SCREEN_WIDTH = 375;
const SCREEN_HEIGHT = 780;
const SCALE = 0.68;

/** Everything waiting on the user, matching the web queue's row count. */
const PENDING_TOTAL = 4;

type BadgeTone = "success" | "warning";

const BADGE_TONES: Record<BadgeTone, { container: string; text: string }> = {
  success: {
    container: "bg-emerald-100 dark:bg-emerald-950",
    text: "text-emerald-700 dark:text-emerald-300",
  },
  warning: {
    container: "bg-amber-100 dark:bg-amber-950",
    text: "text-amber-700 dark:text-amber-300",
  },
};

function Badge({ tone, children }: { tone: BadgeTone; children: ReactNode }) {
  const styles = BADGE_TONES[tone];
  return (
    <div
      className={`flex shrink-0 flex-row items-center rounded-full px-3 py-1 ${styles.container}`}
    >
      <span className={`text-xs font-semibold ${styles.text}`}>{children}</span>
    </div>
  );
}

function Card({ children }: { children: ReactNode }) {
  return (
    <div className="mb-3 rounded-2xl border border-border bg-background p-4 shadow-sm dark:bg-white/[0.05]">
      {children}
    </div>
  );
}

function LogoTile({ src }: { src: string }) {
  return (
    <div className="mr-3 rounded-xl border border-border bg-white p-2.5">
      <img src={src} alt="" className="size-[18px] object-contain" />
    </div>
  );
}

function PendingActionCard({ phase }: { phase: ShowcasePhase }) {
  const { t } = useTranslation("home");
  const done = phase === "done";
  const detailRows = [
    { label: t("heroPreview.phone.to"), value: "Sarah Chen" },
    {
      label: t("heroPreview.phone.subject"),
      value: t("heroPreview.rows.sendEmail.detail"),
    },
  ];
  return (
    <Card>
      <div className="flex flex-row items-start">
        <LogoTile src="/connections/gmail.svg" />
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold text-foreground">
            {t("heroPreview.rows.sendEmail.title")}
          </p>
          <div className="mt-1 flex flex-row items-center">
            <Wrench size={11} color="#94A3B8" />
            <span className="ml-1 text-xs text-muted-foreground">
              gmail_send_email · gmail
            </span>
          </div>
        </div>
        <Badge tone={done ? "success" : "warning"}>
          {done
            ? t("heroPreview.phone.statusApproved")
            : t("heroPreview.phone.statusPending")}
        </Badge>
      </div>

      <div className="mt-3 rounded-xl bg-slate-50 px-3 py-2 dark:bg-white/[0.04]">
        {detailRows.map((row) => (
          <div key={row.label} className="flex flex-row py-0.5">
            <span className="w-24 text-xs text-muted-foreground">
              {row.label}
            </span>
            <span className="flex-1 text-xs text-foreground">{row.value}</span>
          </div>
        ))}
      </div>

      <p className="mt-2 text-[11px] text-muted-foreground">
        {done
          ? t("heroPreview.phone.requested", {
              when: t("heroPreview.rows.sendEmail.when"),
            })
          : t("heroPreview.phone.requestedExpires", {
              requested: t("heroPreview.rows.sendEmail.when"),
              expires: t("heroPreview.rows.sendEmail.expires"),
            })}
      </p>

      {done ? (
        <p className="mt-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
          {t("heroPreview.phone.executionSucceeded")}
        </p>
      ) : (
        <div className="mt-3 flex flex-row gap-2">
          <div className="flex flex-1 flex-row items-center justify-center rounded-xl border border-border py-2.5">
            <X size={14} color="#EF4444" />
            <span className="ml-1.5 text-sm font-semibold text-red-500">
              {t("heroPreview.phone.reject")}
            </span>
          </div>
          <div className="relative flex flex-1 flex-row items-center justify-center rounded-xl bg-emerald-600 py-2.5">
            {phase === "approving" ? (
              <Loader2 className="animate-spin" color="#FFFFFF" size={20} />
            ) : (
              <>
                <Check size={14} color="#FFFFFF" />
                <span className="ml-1.5 text-sm font-semibold text-white">
                  {t("heroPreview.phone.approve")}
                </span>
                {/* Touch ripple: the tap that drives both previews. */}
                <span
                  className="showcase-tap pointer-events-none absolute left-1/2 top-1/2 -ml-6 -mt-6 size-12 rounded-full bg-white"
                  aria-hidden
                />
              </>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

function HumanTaskCard() {
  const { t } = useTranslation("home");
  return (
    <div className="mb-3 rounded-2xl border border-border bg-background p-4 shadow-sm">
      <div className="flex flex-row items-start justify-between">
        <div className="mr-3 flex-1">
          <p className="text-base font-semibold text-foreground">
            {t("heroPreview.rows.refund.title")}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {t("heroPreview.phone.updated", {
              when: t("heroPreview.rows.refund.when"),
            })}
          </p>
        </div>
        <Badge tone="warning">{t("heroPreview.phone.badgePending")}</Badge>
      </div>
      <div className="mt-3 rounded-xl bg-primary/5 p-3 dark:bg-primary/15">
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-primary">
          {t("heroPreview.phone.userAsked")}
        </p>
        <p className="text-sm text-foreground">
          {t("heroPreview.phone.userMessage")}
        </p>
      </div>
      <div className="mt-4 flex flex-row items-center justify-center rounded-xl bg-primary px-4 py-3">
        <MessageSquareText color="#FFFFFF" size={16} />
        <span className="ml-2 text-sm font-semibold text-white">
          {t("heroPreview.phone.respond")}
        </span>
      </div>
    </div>
  );
}

function ApprovalRequestCard() {
  const { t } = useTranslation("home");
  return (
    <Card>
      <div className="flex flex-row items-start">
        <LogoTile src="/connections/googlecalendar.svg" />
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold text-foreground">
            {t("heroPreview.rows.calendar.title")}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t("heroPreview.phone.batchMeta", { count: 3 })}
          </p>
        </div>
        <Badge tone="warning">{t("heroPreview.phone.statusPending")}</Badge>
      </div>
      <div className="mt-3 flex flex-row items-center">
        <span className="flex-1 text-xs font-medium text-primary">
          {t("heroPreview.phone.showItems")}
        </span>
        <ChevronDown size={14} color="#2553F9" />
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground">
        {t("heroPreview.phone.requestedExpires", {
          requested: t("heroPreview.rows.calendar.when"),
          expires: t("heroPreview.rows.calendar.expires"),
        })}
      </p>
      <div className="mt-3 flex flex-row gap-2">
        <div className="flex flex-1 flex-row items-center justify-center rounded-xl border border-border py-2.5">
          <X size={14} color="#EF4444" />
          <span className="ml-1.5 text-sm font-semibold text-red-500">
            {t("heroPreview.phone.rejectAll")}
          </span>
        </div>
        <div className="flex flex-1 flex-row items-center justify-center rounded-xl bg-emerald-600 py-2.5">
          <Check size={14} color="#FFFFFF" />
          <span className="ml-1.5 text-sm font-semibold text-white">
            {t("heroPreview.phone.approveAll")}
          </span>
        </div>
      </div>
    </Card>
  );
}

const TABS: {
  Icon: LucideIcon;
  id: "tabHome" | "tabTasks" | "tabChat" | "tabAgents" | "tabProfile";
  active?: boolean;
}[] = [
  { Icon: House, id: "tabHome" },
  { Icon: ListChecks, id: "tabTasks", active: true },
  { Icon: Sparkles, id: "tabChat" },
  { Icon: Bot, id: "tabAgents" },
  { Icon: User, id: "tabProfile" },
];

/**
 * The system tab bar NativeTabs renders: a glass capsule floating over the
 * list, with the selected tab in its own tinted capsule.
 */
function FloatingTabBar() {
  const { t } = useTranslation("home");
  return (
    <>
      {/* Softens the list where it runs under the bar. */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-background via-background/70 to-transparent" />
      <div className="absolute inset-x-5 bottom-7 flex flex-row rounded-full border border-white/70 bg-white/75 p-1 shadow-[0_10px_30px_-8px_rgba(15,23,42,0.35),0_0_0_0.5px_rgba(15,23,42,0.08)] backdrop-blur-xl dark:border-white/10 dark:bg-zinc-800/75">
        {TABS.map(({ Icon, id, active }) => (
          <div
            key={id}
            className={`flex flex-1 flex-col items-center gap-0.5 rounded-full py-1.5 ${
              active
                ? "bg-slate-900/[0.07] text-primary dark:bg-white/[0.12] dark:text-[#8FA8FF]"
                : "text-foreground/80"
            }`}
          >
            <Icon size={22} strokeWidth={active ? 2.4 : 2} />
            <span className="text-[10px] font-semibold leading-none">
              {t(`heroPreview.phone.${id}`)}
            </span>
          </div>
        ))}
      </div>
      {/* Home indicator */}
      <span className="absolute bottom-2 left-1/2 h-1 w-32 -translate-x-1/2 rounded-full bg-foreground/80" />
    </>
  );
}

const FILTERS = [
  { id: "pending", key: "filterPending" },
  { id: "all", key: "filterAll" },
  { id: "resolved", key: "filterResolved" },
] as const;

function TasksScreen({ phase }: { phase: ShowcasePhase }) {
  const { t } = useTranslation("home");
  const pending = phase === "done" ? PENDING_TOTAL - 1 : PENDING_TOTAL;

  return (
    <div className="relative flex h-full flex-col bg-background text-left">
      {/* iOS status bar */}
      <div className="flex h-12 shrink-0 flex-row items-end justify-between px-8 pb-1.5 text-foreground">
        <span className="text-[15px] font-semibold">9:41</span>
        <span className="flex flex-row items-center gap-1.5">
          <Signal size={15} strokeWidth={2.5} />
          <Wifi size={15} strokeWidth={2.5} />
          <BatteryFull size={20} strokeWidth={2} />
        </span>
      </div>

      <div className="flex min-h-0 flex-1 flex-col px-3 pt-2">
        <div className="mb-4 flex flex-col gap-3">
          <div className="flex flex-row items-center">
            <div className="rounded-2xl bg-primary/10 p-2.5 dark:bg-primary/20">
              <ShieldCheck color="#2553F9" size={20} />
            </div>
            <div className="ml-3 min-w-0 flex-1">
              <p className="text-2xl font-bold text-foreground">
                {t("heroPreview.phone.title")}
              </p>
              <p className="text-sm text-muted-foreground">
                {t("heroPreview.phone.subtitle")}
              </p>
            </div>
          </div>

          <div className="flex flex-row items-center justify-between">
            <div className="flex flex-row items-center">
              <ListFilter color="#64748B" size={16} />
              <span className="ml-2 text-sm font-medium text-muted-foreground">
                {t("heroPreview.phone.filter")}
              </span>
            </div>
            <div className="flex flex-row gap-2">
              {FILTERS.map(({ id, key }) => {
                const selected = id === "pending";
                return (
                  <span
                    key={id}
                    className={`flex flex-row items-center rounded-full px-3 py-1.5 text-xs font-semibold ${
                      selected
                        ? "bg-primary text-white"
                        : "bg-slate-100 text-muted-foreground dark:bg-slate-800"
                    }`}
                  >
                    {t(`heroPreview.phone.${key}`)}
                    {selected ? (
                      <span className="-mr-1 ml-1.5 flex min-w-[18px] justify-center rounded-full bg-white px-1.5 py-0.5 text-[10px] font-bold leading-none text-primary">
                        {pending}
                      </span>
                    ) : null}
                  </span>
                );
              })}
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-hidden">
          <PendingActionCard phase={phase} />
          <HumanTaskCard />
          <ApprovalRequestCard />
        </div>
      </div>

      <FloatingTabBar />
    </div>
  );
}

export function MobileAppPreview({ phase }: { phase: ShowcasePhase }) {
  return (
    <div className="phone-surface relative rounded-[2.75rem] bg-zinc-900 p-2 shadow-[0_0_0_1px_rgba(255,255,255,0.08)_inset,0_30px_60px_-20px_rgba(24,38,72,0.55)] dark:bg-zinc-800">
      <div
        className="relative overflow-hidden rounded-[2.25rem] bg-background"
        style={{ width: SCREEN_WIDTH * SCALE, height: SCREEN_HEIGHT * SCALE }}
      >
        <div
          className="origin-top-left"
          style={{
            width: SCREEN_WIDTH,
            height: SCREEN_HEIGHT,
            transform: `scale(${SCALE})`,
          }}
        >
          <TasksScreen phase={phase} />
        </div>
        {/* Dynamic Island */}
        <span className="absolute left-1/2 top-2 h-5 w-[4.5rem] -translate-x-1/2 rounded-full bg-black" />
      </div>
    </div>
  );
}
